'use client';

import { signIn, signOut, useSession } from 'next-auth/react';
import { useSyncExternalStore } from 'react';
import { RefreshCw } from 'lucide-react';
import { clsx } from 'clsx';
import { sessionNeedsRenewal } from './session-renewal';
import {
  getSessionRejection,
  subscribeSessionRejection,
} from './session-rejected-signal';

/**
 * Erneuert die Anmeldung in einem Schritt.
 *
 * Anders als der Kontowechsel (`completeAccountSwitch`) wird hier bewusst
 * KEIN `prompt=login` gesetzt: lebt die Keycloak-SSO-Session noch, erkennt
 * Keycloak den Nutzer wieder und liefert frische Tokens ohne Passworteingabe.
 * Ist sie wirklich abgelaufen, erscheint die Anmeldemaske — beides führt zum
 * Ziel, der erste Fall aber in einem Klick.
 */
export async function renewSession(callbackUrl = '/'): Promise<void> {
  await signOut({ redirect: false });
  await signIn('keycloak', { callbackUrl });
}

/**
 * Ist diese Session angemeldet, aber ohne brauchbares Token für die API?
 *
 * Zwei Quellen: das Fehlersignal aus dem Refresh (Session selbst) und die
 * Ablehnung durch die API (`session-rejected-signal`). Letztere ist die
 * wichtigere — eine serverseitig beendete Keycloak-Session ist der Session
 * lokal nicht anzusehen, weil das Token formal noch Tage gültig bleibt.
 *
 * Die Regel steht in `session-renewal.ts` und ist dort getestet.
 */
export function useSessionNeedsRenewal(): boolean {
  const { data, status } = useSession();
  const rejection = useSyncExternalStore(
    subscribeSessionRejection,
    getSessionRejection,
    // Serverseitig gibt es keine Ablehnung — sie entsteht erst durch eine
    // Anfrage aus dem Browser.
    () => null,
  );
  return sessionNeedsRenewal(data, status, rejection);
}

interface SessionRenewalNoticeProps {
  /**
   * Zeigt den Erneuern-Button. Bewusst eine explizite Prop: sind mehrere
   * Bereiche einer Seite betroffen (Dashboard: Status-Übersicht UND letzte
   * Belege), soll nur der oberste zum Handeln auffordern, statt dieselbe
   * Aufforderung mehrfach übereinander zu stapeln.
   */
  showAction?: boolean;
  /**
   * `boxed` (Standard) bringt eigenen Rahmen und Hintergrund mit — für
   * Stellen, an denen die Box selbst der Inhalt ist. `plain` lässt beides
   * weg, damit es innerhalb einer schon gerahmten Card keinen doppelten
   * Rahmen gibt (letzte Belege).
   */
  variant?: 'boxed' | 'plain';
  className?: string;
}

/**
 * Tritt an die Stelle der roten „Fehler beim Laden …"-Box, wenn die Ursache
 * eine erneuerungsbedürftige Session ist. Neutral gehalten statt rot: der
 * Nutzer hat nichts falsch gemacht, und die Sache ist mit einem Klick erledigt.
 */
export function SessionRenewalNotice({
  showAction = false,
  variant = 'boxed',
  className,
}: SessionRenewalNoticeProps) {
  return (
    <div
      className={clsx(
        'flex flex-col items-center justify-center text-center px-6 py-8',
        variant === 'boxed' && 'bg-zinc-50 border border-zinc-200 rounded-xl',
        className,
      )}
    >
      <p className="text-secondary font-bold text-base">
        Ihre Sitzung muss erneuert werden
      </p>
      <p className="mt-1.5 text-zinc-500 text-sm max-w-md leading-relaxed">
        Ihre Anmeldung ist abgelaufen und ließ sich nicht automatisch erneuern.
        Deshalb können gerade keine Daten geladen werden. Ihre Favoriten,
        Anfragen und Projekte bleiben unverändert erhalten.
      </p>
      {showAction && (
        <button
          type="button"
          onClick={() => void renewSession(window.location.pathname)}
          className="mt-4 inline-flex items-center gap-2 px-4 py-2 bg-primary text-white text-sm font-bold rounded-lg hover:bg-primary-hover transition-colors"
        >
          <RefreshCw size={16} aria-hidden="true" /> Sitzung erneuern
        </button>
      )}
    </div>
  );
}
